import type { EntityState } from "@reduxjs/toolkit"
import type {
  BaseQueryFn,
  FetchBaseQueryError,
  MutationActionCreatorResult,
  MutationDefinition,
  QueryActionCreatorResult,
  QueryDefinition,
} from "@reduxjs/toolkit/query/react"
import type { Entity, Uuid } from "@/utils"

export type BaseQueryError = FetchBaseQueryError

type Query<QueryResult> = QueryDefinition<
  unknown,
  BaseQueryFn,
  string,
  QueryResult
>

type Mutation<MutationResult> = MutationDefinition<
  unknown,
  BaseQueryFn,
  string,
  MutationResult
>

export type ActionCreatorResult<Result> =
  | QueryActionCreatorResult<Query<Result>>
  | MutationActionCreatorResult<Mutation<Result>>

export type Pager = {
  page?: number
  pageSize?: number
}

export type Pagination = {
  count: number
  currentPage: number
  currentLimit: number
  pageCount: number
  // This is the page list to be displayed if using paginator
  pages: number[]
  totalCount: number
  pageSize: number
}

export type Page<TItem extends Record<string, unknown>> = {
  items: TItem[]
  pagination: Pagination
}

export type EntityStatePage<TItem extends Record<string, unknown>> =
  EntityState<TItem, Uuid> & {
    pagination: Pagination
  }

// For infinite queries
export type PageParam = {
  page: number
  pageCount: number
}

export type RealtimeHeader = {
  channel: string
  event: string
}

export type EntitiesWithRealtime<TEntity extends Entity> = {
  items: TEntity[]
} & RealtimeHeader

export type StartEndPager = {
  start: number
  end: number
}

export type DateRange = {
  from?: Date
  to?: Date
}

// Realtime (Ably) types

export const REALTIME_HEADER = "Realtime"

export enum Operation {
  Insert = "insert",
  Update = "update",
  Delete = "delete",
}

type Insert<TEntity extends Entity> = {
  readonly operation: Operation.Insert
  readonly payload: TEntity
}

type Update<TEntity extends Entity> = {
  readonly operation: Operation.Update
  readonly payload: TEntity
}

type Remove = {
  readonly operation: Operation.Delete
  readonly payload: Entity["id"]
}

export type EntityChange<TEntity extends Entity> = { actorId: Uuid | null } & (
  | Insert<TEntity>
  | Update<TEntity>
  | Remove
)
